import { useState, useCallback } from 'react';

/**
 * Navigation Views
 */
export const VIEWS = {
  CHAT: 'chat',
  TODOS: 'todos',
  PROGRESS_DASHBOARD: 'progress_dashboard',
  ARTICLE_DETAIL: 'article_detail',
  NEWS_DETAIL: 'news_detail',
  ALL_ARTICLES: 'all_articles',
  ALL_NEWS: 'all_news',
  LEARNING_RESOURCES: 'learning_resources',
  COACHING_SCHEDULE: 'coaching_schedule',
  COACH_DASHBOARD: 'coach_dashboard',
  ORG_DASHBOARD: 'org_dashboard',
  DEV_DASHBOARD: 'dev_dashboard',
  SETTINGS: 'settings'
};

/**
 * Custom hook for managing app navigation
 *
 * Provides:
 * - currentView: current active view
 * - viewData: data for the current view (e.g., article slug, news id)
 * - navigateTo: navigate to a new view with optional data
 * - goBack: return to previous view
 * - canGoBack: whether there's a previous view to go back to
 * - isTransitioning: whether a navigation transition is in progress
 */
export function useNavigation(initialView = VIEWS.CHAT) {
  const [currentView, setCurrentView] = useState(initialView);
  const [viewStack, setViewStack] = useState([initialView]);
  const [viewData, setViewData] = useState({});
  const [isTransitioning, setIsTransitioning] = useState(false);

  /**
   * Navigate to a new view
   * @param {string} view - View to navigate to (from VIEWS constant)
   * @param {object} data - Optional data for the view (e.g., { slug: 'article-slug' })
   */
  const navigateTo = useCallback((view, data = {}) => {
    setIsTransitioning(true);

    // Add to view stack
    setViewStack(prev => [...prev, view]);
    setCurrentView(view);
    setViewData(data);

    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 300);
  }, []);

  /**
   * Go back to the previous view
   */
  const goBack = useCallback(() => {
    if (viewStack.length <= 1) return;

    setIsTransitioning(true);

    setViewStack(prev => {
      const newStack = [...prev];
      newStack.pop(); // Remove current view
      const previousView = newStack[newStack.length - 1];
      setCurrentView(previousView);

      // Clear view data when going back
      setViewData({});

      return newStack;
    });

    // Reset transition state after animation
    setTimeout(() => setIsTransitioning(false), 300);
  }, [viewStack]);

  /**
   * Navigate to home/chat view and clear stack
   */
  const goHome = useCallback(() => {
    setIsTransitioning(true);
    setCurrentView(VIEWS.CHAT);
    setViewStack([VIEWS.CHAT]);
    setViewData({});
    setTimeout(() => setIsTransitioning(false), 300);
  }, []);

  /**
   * Check if we can go back
   */
  const canGoBack = viewStack.length > 1;

  return {
    currentView,
    viewData,
    navigateTo,
    goBack,
    goHome,
    canGoBack,
    isTransitioning,
    viewStack
  };
}
